import { create } from 'zustand';
import api, { setAuthToken, clearAuthToken, getAuthToken } from '../lib/api';
import { sanitizeReturnTo } from '../lib/returnTo';
import { getBookUserId, isAssistantUser } from '../lib/actingFor';
import type { User, DevUser, UserRole } from '../types';

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // CRM-137: set when restoreSession's /api/auth/me call comes back 403
  // (deactivated account) — distinct from `error` so SignIn's generic
  // error banner never doubles up with the AccountDeactivated screen.
  accountDeactivated: boolean;
  accountDeactivatedMessage: string | null;

  // Actions
  login: (token: string) => Promise<void>;
  logout: () => void;
  restoreSession: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  clearError: () => void;
  setEmailComposeMode: (mode: 'COMPOSER' | 'MAILTO' | 'GMAIL') => Promise<{ ok: true } | { needsGmail: true }>;

  // Dev auth helpers
  fetchDevUsers: () => Promise<DevUser[]>;
  loginAsDev: (blastUserId: string) => Promise<void>;

  // Role helpers
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (...roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  isManager: () => boolean;
  isSalesRep: () => boolean;
  canViewAllPeople: () => boolean;
  canManageUsers: () => boolean;
  // Whose book the session works: the principal for an assistant, else the user.
  bookUserId: () => string | null;
  isAssistant: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,  // Start loading to check for existing session
  error: null,
  accountDeactivated: false,
  accountDeactivatedMessage: null,

  /**
   * Login with a JWT token
   * Sets the token and fetches user profile
   */
  login: async (token: string) => {
    try {
      set({ isLoading: true, error: null });

      // Set token in API client
      setAuthToken(token);

      // Fetch user profile to verify token
      const response = await api.get('/api/auth/me');
      const user = response.data.user as User;

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Login error:', error);
      clearAuthToken();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.response?.data?.message || 'Failed to authenticate',
      });
      throw error;
    }
  },

  /**
   * Logout - clear all auth state and clear the HttpOnly help-gate cookie server-side.
   */
  logout: () => {
    // Best-effort: clear the HttpOnly help-gate cookie server-side (JS can't).
    api.post('/api/auth/logout').catch(() => {});
    clearAuthToken();
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      accountDeactivated: false,
      accountDeactivatedMessage: null,
    });
  },

  /**
   * Restore session from localStorage token (shared across tabs, survives reloads),
   * or consume a fresh ?token= from the URL (Blast Rails handoff).
   * If a URL token is rejected by the backend, surface a user-visible error
   * so the SignIn page can explain why we're showing it again. If the backend
   * rejects it with 403 (deactivated account), surface AccountDeactivated
   * instead of bouncing back to SignIn.
   */
  restoreSession: async () => {
    // Reset per-attempt state up front — also covers the "Try again" retry
    // from AccountDeactivated, and keeps AuthGuard on the loading spinner
    // (rather than a flash redirect to /sign-in) while the retry is in flight.
    set({ accountDeactivated: false, accountDeactivatedMessage: null, isLoading: true });

    // === Check URL for ?token= (Blast Rails handoff) and ?returnTo= (help-gate bounce) ===
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) {
      setAuthToken(urlToken);
    }
    const safeReturn = sanitizeReturnTo(urlParams.get('returnTo'));
    if (safeReturn) {
      // Stash in sessionStorage so it survives the same-tab SSO round-trip.
      sessionStorage.setItem('crm_return_to', safeReturn);
    }
    if (urlToken || urlParams.has('returnTo')) {
      urlParams.delete('token');
      urlParams.delete('returnTo');
      const cleanSearch = urlParams.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
      window.history.replaceState({}, '', cleanUrl);
    }
    // === END URL param extraction ===

    const existingToken = getAuthToken();
    if (!existingToken) {
      set({ isLoading: false });
      return;
    }
    try {
      const response = await api.get('/api/auth/me');
      const user = response.data.user as User;
      set({
        user,
        token: existingToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      // If we arrived here via the /help/ gate bounce, the cookie is now set
      // (the /api/auth/me call above issued it) — send the user to the guide.
      const pendingReturn = sessionStorage.getItem('crm_return_to');
      if (pendingReturn) {
        sessionStorage.removeItem('crm_return_to');
        window.location.assign(pendingReturn);
        return;
      }
    } catch (error: unknown) {
      clearAuthToken();
      const err = error as { response?: { status?: number; data?: { message?: string } } };
      if (err.response?.status === 403) {
        set({
          isAuthenticated: false,
          isLoading: false,
          accountDeactivated: true,
          accountDeactivatedMessage:
            err.response?.data?.message || 'Your account has been deactivated. Contact an admin for access.',
        });
        return;
      }
      set({
        isLoading: false,
        error: urlToken
          ? 'Sign-in via Blast failed: your access token was rejected. Please try again, or contact an admin if this keeps happening.'
          : null,
      });
    }
  },

  /**
   * Fetch current user profile
   */
  fetchProfile: async () => {
    try {
      const response = await api.get('/api/auth/me');
      set({ user: response.data.user });
    } catch (error: any) {
      console.error('Fetch profile error:', error);
      throw error;
    }
  },

  /**
   * Clear error state
   */
  clearError: () => set({ error: null }),

  /**
   * Update the current user's email compose mode (COMPOSER, GMAIL, or MAILTO).
   * Returns { needsGmail: true } instead of throwing when the backend
   * rejects a handoff-mode switch (GMAIL or MAILTO) because Gmail isn't
   * connected (409).
   */
  setEmailComposeMode: async (mode) => {
    try {
      const { data } = await api.patch('/api/auth/me', { emailComposeMode: mode });
      set({ user: data.user });
      return { ok: true } as const;
    } catch (e: any) {
      if (e.response?.status === 409 && e.response?.data?.code === 'GMAIL_NOT_CONNECTED') {
        return { needsGmail: true } as const;
      }
      throw e;
    }
  },

  // ==================== DEV AUTH HELPERS ====================

  /**
   * Fetch list of dev users for login picker
   */
  fetchDevUsers: async (): Promise<DevUser[]> => {
    if (import.meta.env.VITE_ENABLE_DEV_LOGIN !== 'true') {
      throw new Error('Dev login is disabled in this build');
    }
    try {
      const response = await api.get('/dev/auth/users');
      return response.data.users;
    } catch (error: any) {
      console.error('Fetch dev users error:', error);
      throw error;
    }
  },

  /**
   * Login as a dev user by blastUserId
   */
  loginAsDev: async (blastUserId: string) => {
    if (import.meta.env.VITE_ENABLE_DEV_LOGIN !== 'true') {
      throw new Error('Dev login is disabled in this build');
    }
    try {
      set({ isLoading: true, error: null });

      // Get token from dev auth endpoint
      const response = await api.post('/dev/auth/token', { blastUserId });
      const { token, user } = response.data;

      // Set token in API client
      setAuthToken(token);

      // Convert DevUser to full User (fetch complete profile)
      const profileResponse = await api.get('/api/auth/me');
      const fullUser = profileResponse.data.user as User;

      set({
        user: fullUser,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Dev login error:', error);
      clearAuthToken();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.response?.data?.message || 'Failed to login',
      });
      throw error;
    }
  },

  // ==================== ROLE HELPERS ====================
  // Helpers read `user.roles` (multi-role array sourced from
  // UserRoleAssignment). ADMIN is treated as a superset in hasAnyRole.

  hasRole: (role: UserRole) => {
    const { user } = get();
    return !!user?.roles?.includes(role);
  },

  hasAnyRole: (...roles: UserRole[]) => {
    const { user } = get();
    if (!user?.roles) return false;
    if (user.roles.includes('ADMIN')) return true;
    return roles.some((r) => user.roles.includes(r));
  },

  isAdmin: () => get().hasRole('ADMIN'),
  isManager: () => get().hasRole('MANAGER'),
  isSalesRep: () => get().hasRole('SALES_REP'),
  canViewAllPeople: () => get().hasAnyRole('MANAGER', 'ADMIN', 'MATCHING_SUPPORT'),
  canManageUsers: () => get().hasRole('ADMIN'),
  bookUserId: () => getBookUserId(get().user),
  isAssistant: () => isAssistantUser(get().user),
}));
